import type { PriceFacet } from '../types'

/**
 * Builds a graph facet object with scaled bucket values for price distribution.
 *
 * @param priceFacets - The price facet configuration, including scales and metadata.
 * @param algoliaFacets - Facet value counts from Algolia, keyed by facet name.
 * @param algoliaFacetsValues - Aggregated values (avg and max) for price.
 * @returns An object with facet name, title, avg, max scale, values array, and step size.
 */
export function getGraphFacet(
  priceFacets: PriceFacet,
  algoliaFacets: Record<string, Record<string, number>>,
  algoliaFacetsValues: { avg: number; max: number },
) {
  const RANGE_BAR = 20

  // 1. Sort the scales in ascending order by maxValue
  const sortedScales = priceFacets.scales.toSorted(
    (a, b) => a.maxValue - b.maxValue,
  )

  // 2. Find the first scale whose maxValue is >= the max value from Algolia
  const largestScale = sortedScales[sortedScales.length - 1]
  const selectedScale =
    sortedScales.find((scale) => scale.maxValue >= algoliaFacetsValues.max) ||
    largestScale

  // 3. Retrieve the facet values for the selected scale
  const facets = algoliaFacets[selectedScale.facetName] || {}

  // 4. Build the values array, filling missing buckets with 0
  const values: number[] = []
  for (let i = 0; i < RANGE_BAR; i += 1) {
    // facets keys are numbers in string form ("0", "1", ...)
    values.push(facets[i] ?? 0)
  }

  // 5. Calculate the step
  const step = Math.floor(selectedScale.maxValue / RANGE_BAR)

  return {
    name: priceFacets.name,
    title: priceFacets.title,
    avg: algoliaFacetsValues.avg,
    max: selectedScale.maxValue,
    values,
    step,
  }
}
